import React, { useMemo } from "react";
import { format, addMonths, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Calendar, TrendingUp, Calculator, Lock, Unlock } from "lucide-react";

export default function ProjecaoCustos({ custosAlocados, lancamentos }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Separar custos fixos e variáveis
  const custosPorTipo = useMemo(() => {
    const fixos = custosAlocados.filter(c => c.tipo_custo === 'fixo');
    const variaveis = custosAlocados.filter(c => c.tipo_custo === 'variavel');
    
    return {
      fixos: {
        lista: fixos,
        total: fixos.reduce((a, c) => a + (c.valor || 0), 0)
      },
      variaveis: {
        lista: variaveis,
        total: variaveis.reduce((a, c) => a + (c.valor || 0), 0)
      }
    };
  }, [custosAlocados]);

  // Projeção mensal (próximos 12 meses)
  const projecaoMensal = useMemo(() => {
    const meses = [];
    const hoje = new Date();
    
    for (let i = 0; i < 12; i++) {
      const data = addMonths(hoje, i);
      const mesAno = format(data, 'yyyy-MM');
      
      let custosFixos = 0;
      let custosVariaveis = 0;
      let custosRecorrentes = 0;
      
      custosAlocados.forEach(custo => {
        if (!custo.recorrente) return;
        
        const dataInicio = parseISO(custo.data);
        const mesesProjecao = custo.projecao_meses || 12;
        const dataFim = addMonths(dataInicio, mesesProjecao);
        
        if (data >= dataInicio && data <= dataFim) {
          const freq = custo.frequencia || 'mensal';
          let aplicar = false;
          
          const diffMeses = Math.floor((data - dataInicio) / (1000 * 60 * 60 * 24 * 30));
          
          switch (freq) {
            case 'mensal': aplicar = true; break;
            case 'bimestral': aplicar = diffMeses % 2 === 0; break;
            case 'trimestral': aplicar = diffMeses % 3 === 0; break;
            case 'semestral': aplicar = diffMeses % 6 === 0; break;
            case 'anual': aplicar = diffMeses % 12 === 0; break;
            default: aplicar = true;
          }
          
          if (aplicar) {
            custosRecorrentes += custo.valor || 0;
            if (custo.tipo_custo === 'fixo') {
              custosFixos += custo.valor || 0;
            } else {
              custosVariaveis += custo.valor || 0;
            }
          }
        }
      });
      
      // Adicionar custos não-recorrentes do mês
      custosAlocados.forEach(custo => {
        if (custo.recorrente) return;
        const dataCusto = custo.data?.substring(0, 7);
        if (dataCusto === mesAno) {
          if (custo.tipo_custo === 'fixo') {
            custosFixos += custo.valor || 0;
          } else {
            custosVariaveis += custo.valor || 0;
          }
        }
      });
      
      meses.push({
        mes: format(data, 'MMM/yy', { locale: ptBR }),
        fixos: custosFixos,
        variaveis: custosVariaveis,
        total: custosFixos + custosVariaveis
      });
    }
    
    return meses;
  }, [custosAlocados]);

  // Totais projetados
  const totaisProjetados = useMemo(() => {
    const totalFixos = projecaoMensal.reduce((a, m) => a + m.fixos, 0);
    const totalVariaveis = projecaoMensal.reduce((a, m) => a + m.variaveis, 0);
    return {
      fixos: totalFixos,
      variaveis: totalVariaveis,
      total: totalFixos + totalVariaveis,
      mediaFixos: totalFixos / 12,
      mediaVariaveis: totalVariaveis / 12
    };
  }, [projecaoMensal]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-medium text-stone-800 mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-sm" style={{ color: p.color }}>
              {p.name}: {formatCurrency(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cards Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4" />
              <p className="text-blue-100 text-xs">Custos Fixos (12m)</p>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totaisProjetados.fixos)}</p>
            <p className="text-xs text-blue-200 mt-1">Média: {formatCurrency(totaisProjetados.mediaFixos)}/mês</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Unlock className="w-4 h-4" />
              <p className="text-orange-100 text-xs">Custos Variáveis (12m)</p>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totaisProjetados.variaveis)}</p>
            <p className="text-xs text-orange-200 mt-1">Média: {formatCurrency(totaisProjetados.mediaVariaveis)}/mês</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calculator className="w-4 h-4" />
              <p className="text-purple-100 text-xs">Total Projetado</p>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totaisProjetados.total)}</p>
            <p className="text-xs text-purple-200 mt-1">Próximos 12 meses</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-stone-600 to-stone-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4" />
              <p className="text-stone-300 text-xs">% Custos Fixos</p>
            </div>
            <p className="text-xl font-bold">
              {totaisProjetados.total > 0 ? ((totaisProjetados.fixos / totaisProjetados.total) * 100).toFixed(1) : 0}%
            </p>
            <p className="text-xs text-stone-400 mt-1">do custo total</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Projeção */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Projeção de Custos (12 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={projecaoMensal} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis 
                  tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="fixos" 
                  name="Custos Fixos"
                  stackId="1"
                  stroke="#3b82f6" 
                  fill="#93c5fd"
                />
                <Area 
                  type="monotone" 
                  dataKey="variaveis" 
                  name="Custos Variáveis"
                  stackId="1"
                  stroke="#f97316" 
                  fill="#fdba74"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Listas de Custos por Tipo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Custos Fixos */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-4 h-4 text-blue-600" />
              Custos Fixos
              <Badge className="bg-blue-100 text-blue-700">{custosPorTipo.fixos.lista.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {custosPorTipo.fixos.lista.length === 0 ? (
              <p className="text-stone-500 text-center py-4">Nenhum custo fixo cadastrado</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {custosPorTipo.fixos.lista.map(custo => (
                  <div key={custo.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium text-stone-800">{custo.descricao}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{custo.categoria}</Badge>
                        {custo.recorrente && <Badge variant="outline" className="text-xs">{custo.frequencia}</Badge>}
                      </div>
                    </div>
                    <p className="font-bold text-blue-700">{formatCurrency(custo.valor)}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-stone-200 flex justify-between">
              <span className="font-medium text-stone-600">Total Fixos</span>
              <span className="font-bold text-blue-700">{formatCurrency(custosPorTipo.fixos.total)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Custos Variáveis */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Unlock className="w-4 h-4 text-orange-600" />
              Custos Variáveis
              <Badge className="bg-orange-100 text-orange-700">{custosPorTipo.variaveis.lista.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {custosPorTipo.variaveis.lista.length === 0 ? (
              <p className="text-stone-500 text-center py-4">Nenhum custo variável cadastrado</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {custosPorTipo.variaveis.lista.map(custo => (
                  <div key={custo.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div>
                      <p className="font-medium text-stone-800">{custo.descricao}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{custo.categoria}</Badge>
                        {custo.recorrente && <Badge variant="outline" className="text-xs">{custo.frequencia}</Badge>}
                      </div>
                    </div>
                    <p className="font-bold text-orange-700">{formatCurrency(custo.valor)}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-stone-200 flex justify-between">
              <span className="font-medium text-stone-600">Total Variáveis</span>
              <span className="font-bold text-orange-700">{formatCurrency(custosPorTipo.variaveis.total)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}